import { Modal, useModals } from 'almanac-ui/Modal'
import { Default } from './Screens/Default/Default'
import { AssetAccessGrantWithParent, Permission, RecipientsSelectorValueType } from 'types'
import Styles from './ShareSheetV2.module.scss'
import { FileWithIdAndType } from 'api/clients/AccessGrantsClient'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { observer } from 'mobx-react-lite'
import { EmailValue, Value as RecipientsSelectorValue } from 'components/ShareSheet/RecipientSelector/Types'
import { createContext, useContext, useEffect, useState } from 'react'
import { useAuth } from 'hooks/UseAuth'
import { PublicAccess } from './Screens/PublicAccess'
import { Heading } from 'blaze-ui/Heading/Heading'
import { frontendOrigin } from 'utils/frontendOrigin'
import { Link } from 'almanac-ui/icons'
import { copyTextToClipboard } from 'utils/articleUtils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { InviteToWorkspace } from 'components/ShareSheet/InviteToWorkspace'
import { ConfirmPriceIncreaseModal } from 'components/ConfirmPriceIncreaseModal/ConfirmPriceIncreaseModal'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { usePaymentPlans } from 'hooks/usePaymentPlans'
import { TransferOwnershipConfirmation } from 'components/ShareSheet/TransferOwnershipConfirmation'
import { TakeOwnershipConfirmation } from 'components/ShareSheet/TakeOwnershipConfirmation'
import { EditAccess } from './Screens/EditAccess'
import { InvitePeople } from './Screens/InvitePeople'
import { ReviewSent } from './Screens/ReviewSent'
import { Button } from 'blaze-ui/Button'
import { useInformalFeedbackRequests } from 'hooks/almanacFiles/useInformalFeedbackRequests'
import { Loading } from './Screens/Loading/Loading'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'
import { MobileHeader } from './Header/MobileHeader'

export enum ShareSheetScreen {
  Default = 'default',
  InviteAccess = 'inviteAccess',
  EditAccess = 'editAccess',
  PublicAccess = 'publicAccess',
  ShowInviteToWorkspaceScreen = 'showInviteToWorkspaceScreen',
  ReviewSent = 'ReviewSent',
}

export interface ShareSheetV2Props {
  file: FileWithIdAndType
  initialPermission?: Permission
  initialRecipients?: RecipientsSelectorValue[]
}

export const ShareSheetContext = createContext<{
  file: FileWithIdAndType | null
  asset: AssetAccessGrantWithParent | null
  screen: ShareSheetScreen
  setScreen: (screen: ShareSheetScreen) => void
  isPublishableToWeb: boolean
}>({
  file: null,
  asset: null,
  screen: ShareSheetScreen.Default,
  setScreen: () => {},
  isPublishableToWeb: true,
})

export const useShareSheetContext = () => {
  return useContext(ShareSheetContext) as {
    file: FileWithIdAndType
    asset: AssetAccessGrantWithParent
    screen: ShareSheetScreen
    setScreen: (screen: ShareSheetScreen) => void
    isPublishableToWeb: boolean
  }
}

export const ShareSheetV2 = observer(({ file, initialPermission, initialRecipients }: ShareSheetV2Props) => {
  const auth = useAuth()
  const [screen, setScreen] = useState<ShareSheetScreen>(ShareSheetScreen.Default)
  const { data: currentPlan } = useCurrentPlan()
  const { paymentPlans } = usePaymentPlans()
  const { isMobile } = useResponsiveDesign()
  const workspace = useCurrentWorkspace()
  const { openModal } = useModals()
  const isPublishableToWeb = file.type !== 'design'

  const {
    loadShareSheet,
    loading,
    asset,
    recipients,
    inviteUsers,
    inviteToWorkspace,
    isSubmitting,
    setShareSheet,
    transferOwnershipUser,
    takeOwnership,
  } = useShareSheetV2(file)
  const fullUrl = `${frontendOrigin()}${asset?.url}`

  const { openRequest, isLoading: isLoadingReviews } = useInformalFeedbackRequests(file.id)

  useEffect(() => {
    loadShareSheet({
      initialPermission,
      initialRecipients,
    })
  }, [auth?.id, file, loadShareSheet, initialPermission, initialRecipients])

  const copyLink = () => {
    copyTextToClipboard(fullUrl)
    showToastMessage('Link copied to the clipboard!', 'success')
  }

  const showCopyLink =
    [ShareSheetScreen.Default, ShareSheetScreen.PublicAccess].includes(screen) ||
    (screen === ShareSheetScreen.EditAccess && recipients.length === 0) ||
    (screen === ShareSheetScreen.InviteAccess && openRequest)

  if (loading || isLoadingReviews || !asset || !file) {
    return (
      <Modal.Root>
        <Loading />
      </Modal.Root>
    )
  }

  const handleInviteToWorkspace = () => {
    if (!workspace || !currentPlan) return

    const plan = paymentPlans.find((p) => p.key === currentPlan.plan)

    const isBillingMonthly = currentPlan.billing_frequency === 'month'
    const newMemberCount = recipients.filter((rec) => rec.type === RecipientsSelectorValueType.Email).length
    const isPriceIncrease = plan?.member_limit && workspace.members_count + newMemberCount > plan.member_limit

    if (inviteToWorkspace && isPriceIncrease && !workspace.early_adopter) {
      openModal(ConfirmPriceIncreaseModal, {
        currentPlan: currentPlan.plan,
        onConfirm: inviteUsers,
        newMemberCount,
        isBillingMonthly,
      })
      return
    }

    inviteUsers()
    setScreen(ShareSheetScreen.Default)
  }

  if (takeOwnership && asset && auth) {
    return <TakeOwnershipConfirmation newOwnerId={auth.id} file={file} />
  }

  if (!!transferOwnershipUser && asset) {
    return <TransferOwnershipConfirmation newOwner={transferOwnershipUser} file={file} />
  }

  return (
    <ShareSheetContext.Provider value={{ file, asset, screen, setScreen, isPublishableToWeb }}>
      <ConditionalWrapper condition={!isMobile} wrapper={(children) => <Modal.Root>{children}</Modal.Root>}>
        <div className={Styles.shareSheetBody}>
          <ResponsiveDesign.Mobile>
            <MobileHeader request={openRequest} />
          </ResponsiveDesign.Mobile>
          {screen === ShareSheetScreen.Default && <Default asset={asset} request={openRequest} />}
          {screen === ShareSheetScreen.InviteAccess &&
            (!openRequest ? <InvitePeople /> : <EditAccess title="Invite People" />)}
          {screen === ShareSheetScreen.EditAccess && <EditAccess />}
          {screen === ShareSheetScreen.ReviewSent && <ReviewSent request={openRequest} />}
          {screen === ShareSheetScreen.PublicAccess && <PublicAccess />}
          {screen === ShareSheetScreen.ShowInviteToWorkspaceScreen && (
            <InviteToWorkspace
              onSubmit={handleInviteToWorkspace}
              goBack={() => setScreen(ShareSheetScreen.EditAccess)}
              isSubmitting={isSubmitting}
              inviteToWorkspace={inviteToWorkspace}
              setInviteToWorkspace={(invite: boolean) => setShareSheet({ inviteToWorkspace: invite })}
              newRecipients={recipients.filter((r) => r.type === RecipientsSelectorValueType.Email) as EmailValue[]}
            />
          )}
          {showCopyLink && (
            <div className={Styles.collaborationLink}>
              <ResponsiveDesign.Mobile>
                <Text variant="smallList" color="var(--dark-80)">
                  Shareable Link
                </Text>
              </ResponsiveDesign.Mobile>
              <ResponsiveDesign.Desktop>
                <Heading level={5}>Shareable Link</Heading>
              </ResponsiveDesign.Desktop>
              <div className={Styles.linkWithButton}>
                <div className={Styles.linkContainer}>{fullUrl}</div>
                <Button
                  variant="secondary"
                  fullWidth={isMobile}
                  size={isMobile ? 'xl' : 'md'}
                  frontIcon={Link}
                  onPress={copyLink}
                >
                  Copy Link
                </Button>
              </div>
            </div>
          )}

          {(screen === ShareSheetScreen.PublicAccess ||
            (screen === ShareSheetScreen.EditAccess && recipients.length === 0)) && (
            <Modal.Footer>
              <Modal.FooterContent slot="left">
                <Button size="lg" variant="tertiary" onPress={() => setScreen(ShareSheetScreen.Default)}>
                  Back
                </Button>
              </Modal.FooterContent>
            </Modal.Footer>
          )}
        </div>
      </ConditionalWrapper>
    </ShareSheetContext.Provider>
  )
})
